import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { api, errText } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Copy, Smartphone } from 'lucide-react';

// `required`: the platform requires MFA and this user hasn't enrolled — the
// dialog can't be dismissed, and the user is refreshed only after the backup
// codes are saved (refreshing at enable would unmount it before they show).
export function MfaDialog({ open, onOpenChange, required = false }) {
  const { user, refreshUser } = useApp();
  const [step, setStep] = useState('status'); // status | setup | codes
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('status');
      setSetup(null);
      setCode('');
      setBackupCodes([]);
      setDisablePw('');
      setDisableCode('');
    }
  }, [open]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/mfa/setup');
      setSetup(r.data);
      setCode('');
      setStep('setup');
    } catch (err) {
      toast.error(errText(err, 'Could not start setup'));
    } finally { setBusy(false); }
  };

  const enable = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const r = await api.post('/auth/mfa/enable', { code });
      setBackupCodes(r.data.backup_codes || []);
      setStep('codes');
      if (!required) refreshUser();
      toast.success('Two-factor authentication enabled');
    } catch (err) {
      toast.error(errText(err, 'Invalid code — try again'));
      setCode('');
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post('/auth/mfa/disable', { password: disablePw, code: disableCode });
      toast.success('Two-factor authentication disabled');
      refreshUser();
      onOpenChange(false);
    } catch (err) {
      toast.error(errText(err, 'Could not disable — check your password and code'));
    } finally { setBusy(false); }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      toast.success('Backup codes copied — store them somewhere safe');
    } catch (e) {
      toast.error('Could not copy — write them down manually');
    }
  };

  return (
    <Dialog open={open} onOpenChange={required ? () => {} : onOpenChange}>
      <DialogContent data-testid="mfa-dialog">
        <DialogHeader>
          <DialogTitle>Two-factor authentication</DialogTitle>
          <DialogDescription>
            {required
              ? 'Two-factor authentication is required for every account. Set it up with an authenticator app (Google Authenticator, Authy, 1Password…) to continue.'
              : 'Protect your account with an authenticator app (Google Authenticator, Authy, 1Password…).'}
          </DialogDescription>
        </DialogHeader>

        {step === 'status' && !user?.mfa_enabled && (
          <div className="space-y-4" data-testid="mfa-status-off">
            <div className="rounded-lg border p-3 flex items-start gap-3">
              <Smartphone className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
              <div className="text-sm text-muted-foreground">
                After setup, signing in requires your password <span className="font-medium text-foreground">plus a 6-digit code</span> from your phone. You'll also get backup codes in case you lose the device.
              </div>
            </div>
            <Button onClick={startSetup} disabled={busy} className="w-full" data-testid="mfa-start-setup-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Set up authenticator
            </Button>
          </div>
        )}

        {step === 'status' && user?.mfa_enabled && (
          <div className="space-y-4" data-testid="mfa-status-on">
            <div className="rounded-lg border p-3 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
              <div className="text-sm font-medium">Two-factor authentication is <Badge className="ml-1">Enabled</Badge></div>
            </div>
            <form onSubmit={disable} className="space-y-3">
              <p className="text-xs text-muted-foreground">To turn it off, confirm your password and a current code (or a backup code).</p>
              <div className="space-y-1.5">
                <Label htmlFor="mfa-disable-pw" className="text-xs font-medium">Password</Label>
                <Input id="mfa-disable-pw" type="password" required autoComplete="current-password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} data-testid="mfa-disable-password" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mfa-disable-code" className="text-xs font-medium">Authenticator or backup code</Label>
                <Input id="mfa-disable-code" required value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456 or XXXX-XXXX" className="font-mono" data-testid="mfa-disable-code" />
              </div>
              <Button type="submit" variant="destructive" disabled={busy} className="w-full" data-testid="mfa-disable-submit">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Disable two-factor authentication
              </Button>
            </form>
          </div>
        )}

        {step === 'setup' && setup && (
          <form onSubmit={enable} className="space-y-4" data-testid="mfa-setup-step">
            <ol className="text-sm text-muted-foreground list-decimal ml-4 space-y-1">
              <li>Open your authenticator app and scan this QR code.</li>
              <li>Enter the 6-digit code it shows to confirm.</li>
            </ol>
            <div className="flex justify-center rounded-lg border bg-white p-4">
              <QRCodeSVG value={setup.otpauth_uri} size={168} data-testid="mfa-qr-code" />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Can't scan? Enter this key manually: <span className="font-mono font-medium text-foreground break-all">{setup.secret}</span>
            </p>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-enable-otp">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button type="submit" className="w-full" disabled={busy || code.length < 6} data-testid="mfa-enable-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify & enable
            </Button>
          </form>
        )}

        {step === 'codes' && (
          <div className="space-y-4" data-testid="mfa-backup-codes-step">
            <div className="rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-3 text-sm">
              <span className="font-semibold">Save these backup codes now — they won't be shown again.</span> Each works once if you lose your authenticator.
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="mfa-backup-codes-list">
              {backupCodes.map((c) => (
                <div key={c} className="rounded-md border bg-card px-3 py-1.5 font-mono text-sm text-center">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={copyCodes} data-testid="mfa-copy-codes-button">
                <Copy className="h-4 w-4 mr-1" /> Copy all
              </Button>
              <Button className="flex-1" onClick={() => { if (required) refreshUser(); onOpenChange(false); }} data-testid="mfa-done-button">
                I've saved them
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
